import { graphql, fetchQuery, type Environment } from 'react-relay';
import { OPERAND_TYPE_KEYWORD, OPERAND_TYPE_VALUE } from '@atlaskit/jql-ast';
import type {
	EndCursor,
	Option,
	OptionResponse,
	SearchTemplate,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type {
	sprintJqlBuilderBasicFieldValuesQuery,
	sprintJqlBuilderBasicFieldValuesQuery$data as sprintPropsResolver$data,
	JiraJqlViewContext,
} from '@atlassian/jira-relay/src/__generated__/sprintJqlBuilderBasicFieldValuesQuery.graphql';
import type { sprintJqlBuilderBasicInitQuery } from '@atlassian/jira-relay/src/__generated__/sprintJqlBuilderBasicInitQuery.graphql';
import type { FormatMessage, CloudId } from '@atlassian/jira-shared-types/src/general.tsx';
import { OperandValues } from '../../../../../../common/constants.tsx';
import type { PropsResolver, PropTypes } from '../../../../../../common/types.tsx';
import {
	fireJqlBuilderBasicErrorAnalytics,
	generateErrorOptions,
	generateOptions,
} from '../../../../../../common/utils.tsx';
import { getEmptyLabel } from '../../../../../../utils/get-empty-label/index.tsx';
import messages from './messages.tsx';
import { hasValue, getSprintState } from './utils.tsx';

type Params = {
	fieldId: string;
	propType: PropTypes;
	searchTemplate: SearchTemplate;
	formatMessage: FormatMessage;
	jql?: string;
	environment: Environment;
	cloudId: CloudId;
	operandDisplayValues?: string[];
	viewContext: JiraJqlViewContext | null;
};

type GetSuggestedParams = {
	environment: Environment;
	cloudId: CloudId;
	fieldId: string;
	searchTemplate: SearchTemplate;
	endCursor: EndCursor;
	formatMessage: FormatMessage;
};

/*
    On picker search, we also need to know JQL and search parameters
*/
type GetFieldValueParams = {
	environment: Environment;
	cloudId: CloudId;
	fieldId: string;
	searchTemplate: SearchTemplate;
	jql?: string;
	search: string;
	endCursor: EndCursor;
	formatMessage: FormatMessage;
	viewContext: JiraJqlViewContext | null;
};

type SprintNode = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<
				NonNullable<NonNullable<sprintPropsResolver$data['jira']>['jqlBuilder']>['fieldValues']
			>['edges']
		>[number]
	>['node']
>;

export const getOption = (
	node: SprintNode,
	formatMessage: FormatMessage,
	itemType: 'value',
): Option => {
	const { displayName, jqlTerm } = node;

	const option: Option = {
		label: displayName,
		value: jqlTerm,
		optionType: 'option',
		itemType,
	};

	if (node.sprint) {
		const { state, boardName } = node.sprint;
		const i18nState = state ? getSprintState(state, formatMessage) : undefined;
		const secondaryLabel = [i18nState, boardName].filter(hasValue).join(' ⋅ ');

		if (secondaryLabel !== '') {
			return {
				...option,
				secondaryLabel,
			};
		}
	}

	return option;
};

const getSuggestedOptions = async ({
	environment,
	cloudId,
	searchTemplate,
	endCursor,
	formatMessage,
}: GetSuggestedParams): Promise<OptionResponse> => {
	const data = await fetchQuery<sprintJqlBuilderBasicInitQuery>(
		environment,
		graphql`
			query sprintJqlBuilderBasicInitQuery($cloudId: ID!) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						recentlyUsedSprints(first: 5) {
							edges {
								node {
									jqlTerm
									displayName
									sprint {
										state
										boardName
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();

	const emptyOption: Option[] = [
		{
			label: getEmptyLabel(searchTemplate ?? null, formatMessage),
			value: OperandValues.empty.value,
			optionType: 'option',
			itemType: OPERAND_TYPE_KEYWORD,
		},
	];
	if (!data?.jira?.jqlBuilder?.recentlyUsedSprints?.edges) {
		return generateOptions(emptyOption);
	}

	const { edges: fieldValues } = data.jira.jqlBuilder.recentlyUsedSprints;
	const sprints = fieldValues.reduce<Array<Option>>((options, edges) => {
		if (!edges || !edges.node) {
			return options;
		}

		const { node } = edges;
		options.push(getOption(node, formatMessage, OPERAND_TYPE_VALUE));

		return options;
	}, []);
	return generateOptions([
		...(!endCursor ? emptyOption : []),
		{
			label: formatMessage(messages.refinementOptionSuggestedSprints),
			optionType: 'group',
			options: sprints,
		},
	]);
};
const getFieldValueOptions = async ({
	environment,
	cloudId,
	fieldId,
	searchTemplate,
	jql,
	search,
	endCursor,
	formatMessage,
	viewContext,
}: GetFieldValueParams): Promise<OptionResponse> => {
	const after = endCursor;

	const data = await fetchQuery<sprintJqlBuilderBasicFieldValuesQuery>(
		environment,
		graphql`
			query sprintJqlBuilderBasicFieldValuesQuery(
				$cloudId: ID!
				$first: Int = 10
				$jqlTerm: String!
				$jql: String!
				$searchString: String!
				$after: String
				$viewContext: JiraJqlViewContext
			) {
				jira @required(action: THROW) {
					jqlBuilder(cloudId: $cloudId) {
						fieldValues(
							first: $first
							jqlTerm: $jqlTerm
							jqlContext: $jql
							searchString: $searchString
							after: $after
							viewContext: $viewContext
						) {
							totalCount
							pageInfo {
								endCursor
							}
							edges {
								node {
									jqlTerm
									displayName
									... on JiraJqlSprintFieldValue {
										sprint {
											state
											boardName
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			cloudId,
			first: 10,
			jqlTerm: fieldId.toLowerCase(),
			jql: jql || '',
			searchString: search,
			after,
			viewContext,
		},
		{ fetchPolicy: 'store-or-network' },
	).toPromise();

	const emptyOption: Option = {
		label: getEmptyLabel(searchTemplate ?? null, formatMessage),
		value: OperandValues.empty.value,
		optionType: 'option',
		itemType: OPERAND_TYPE_KEYWORD,
	};

	if (!data?.jira?.jqlBuilder?.fieldValues?.edges) {
		return generateOptions([emptyOption]);
	}

	const { edges: fieldValues, totalCount, pageInfo } = data.jira.jqlBuilder.fieldValues;
	const sprints = fieldValues.reduce<Array<Option>>((options, edges) => {
		if (!edges || !edges.node) {
			return options;
		}

		const { node } = edges;
		options.push(getOption(node, formatMessage, OPERAND_TYPE_VALUE));

		return options;
	}, []);

	return generateOptions(
		[...(search.length === 0 && !endCursor ? [emptyOption] : []), ...sprints],
		totalCount,
		pageInfo?.endCursor || undefined,
	);
};

export const getSprintProps = ({
	fieldId,
	formatMessage,
	jql,
	environment,
	cloudId,
	operandDisplayValues,
	searchTemplate,
	viewContext,
}: Params): PropsResolver => ({
	enableFullList: true,
	operandDisplayValues,
	showOperandDisplayValuesInFullList: true,
	loadOptions: async (search, renderInitialValues, endCursor) => {
		try {
			if (search.length === 0 && renderInitialValues) {
				return await getSuggestedOptions({
					environment,
					cloudId,
					fieldId,
					searchTemplate,
					endCursor,
					formatMessage,
				});
			}
			return await getFieldValueOptions({
				environment,
				cloudId,
				fieldId,
				searchTemplate,
				jql,
				search,
				endCursor,
				formatMessage,
				viewContext,
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireJqlBuilderBasicErrorAnalytics('sprintJqlBuilderBasicFieldValues', searchTemplate, error);
			return generateErrorOptions(error);
		}
	},
});
